import { useGetOffersCargos } from "@/entities/get-offers-cargos/fetch";
import type { OfferCargoProcess } from "@/features/offer-cargo-process-status";
import { type Loadable, transformLoadables } from "@/shared/lib/loadable";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import type { FreightMatching } from "@x-mile/api-contract";
import { useCallback, useMemo } from "react";

const fixVehicleWeightWorkaround = (
  offerCargo: FreightMatching.schemas.OfferCargo,
): FreightMatching.schemas.OfferCargo => {
  // workaround: vehicleWeightがstringで帰ってくるのでnumberに変換する
  const vehicleWeight = offerCargo.vehicleWeight;
  const vehicleWeightNumber = Number(vehicleWeight);
  if (Number.isNaN(vehicleWeightNumber)) {
    throw new Error(
      `vehicleWeight is not a number: ${vehicleWeight} offerCargoId: ${offerCargo.id}`,
    );
  }
  return {
    ...offerCargo,
    vehicleWeight: vehicleWeightNumber,
  };
};

export const fetchOfferCargoProcess = (
  offerCargoProcessId: number | undefined,
): {
  offerCargoProcess: Loadable<OfferCargoProcess>;
  refetch: () => void;
} => {
  const getOfferCargosInProcessQuery = useGetOffersCargos(
    {
      processId: offerCargoProcessId,
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: !!offerCargoProcessId,
    },
  );

  const offerCargoProcess: Loadable<OfferCargoProcess> = useMemo(() => {
    return transformLoadables(
      [
        loadableFromUseQueryResult({
          data: getOfferCargosInProcessQuery.data,
          status: getOfferCargosInProcessQuery.status,
          error: getOfferCargosInProcessQuery.error,
        }),
      ],
      (offerCargosInProcess) => {
        if (!offerCargosInProcess.data.cargos) {
          throw new Error(
            `Offer cargos in process not found in getOfferCargosInProcessQuery process id: ${offerCargoProcessId}`,
          );
        }
        const draftOfferCargo = offerCargosInProcess.data.cargos.find(
          (offerCargo) => offerCargo.stage === "draft",
        );
        if (!draftOfferCargo) {
          throw new Error(
            `Draft offer cargo not found in getOfferCargosInProcessQuery process id: ${offerCargoProcessId}`,
          );
        }
        const deliveryProposalOfferCargos =
          offerCargosInProcess.data.cargos.filter(
            (offerCargo) => offerCargo.stage === "delivery_proposal",
          );
        const contractOfferCargos = offerCargosInProcess.data.cargos.filter(
          (offerCargo) =>
            offerCargo.stage === "contract_proposal" ||
            offerCargo.stage === "contract_concluded",
        );
        return {
          draftOfferCargo: fixVehicleWeightWorkaround(draftOfferCargo),
          deliveryProposalOfferCargos: deliveryProposalOfferCargos.map(
            fixVehicleWeightWorkaround,
          ),
          contractOfferCargos: contractOfferCargos.map(
            fixVehicleWeightWorkaround,
          ),
        };
      },
    );
  }, [
    getOfferCargosInProcessQuery.status,
    getOfferCargosInProcessQuery.data,
    getOfferCargosInProcessQuery.error,
    offerCargoProcessId,
  ]);
  const refetch = useCallback(() => {
    getOfferCargosInProcessQuery.refetch();
  }, [getOfferCargosInProcessQuery]);
  return {
    offerCargoProcess,
    refetch,
  };
};
